import { CronExpressionParser } from "cron-parser"

interface IteratorProps {
  start?: number
  freq?: number
}

export const DEFAULT_SCHEDULE_FREQUENCY = 24

/**
 * Generates an array of values for use in a crontab expression's hour property.
 * Given a start & frequency, increment each hour by the frequency step until
 * the current value exceeds 23 (number of 0-indexed hours in a day),
 * at which point loop from 0 and continue until reaching start.
 * Example:
 * given {start: 3, freq: 6}
 * result: 3,9,15,21
 * @param start
 * @param freq
 * @returns {string} Sorted string of the hours, e.g.: "3,9,15,21" or * if no values provided
 */
export function generateCronWithCyclicHours({ start, freq }: IteratorProps): string {
  const range = []
  if (start !== undefined && freq !== undefined) {
    // Create a new date object to get the current UTC hour
    const UTCHour = new Date()
    // Set the current hour to the start value
    UTCHour.setHours(start, 0, 0, 0)
    // Get the current hour in UTC
    const startOffset = UTCHour.getUTCHours()
    // Initialize the current hour to the UTC-converted start value
    let current = startOffset
    do {
      range.push(current)
      current = (current + freq) % 24
    } while (current !== startOffset)
    const hours = range.sort((a, b) => a - b).join(",")
    return `0 ${hours} * * ? *`
  }

  // default to 0, i.e. 0th minute of the 0th hour / midnight
  return "0 0 * * ? *"
}
/**
 * Given a 5-value cron expression, parse out the hours and calculate the smallest interval
 * between them (this accounts for any possible variations, but there shouldn't be any)
 * @param cronExp
 * @returns {number} The integer value of the interval between hours
 */
export function getHourlyIntervalFromCron(cronExp: string): number {
  const cronArr = cronExp.split(" ")
  const hours: number[] = cronArr[1]
    .split(",")
    .map((v) => parseInt(v))
    .sort((a, b) => a - b)

  // Since 24 hours is currently our broadest interval, default to it
  // to account for cron expressions like "0 10 * * *"
  // where the hours[i + 1] - hours[i] results in a NaN or other falsy value
  let interval = 24

  for (let i = 0; i < hours.length - 1; i++) {
    const currInterval = hours[i + 1] - hours[i]
    if (currInterval < interval) {
      interval = currInterval
    }
  }
  return interval
}

/**
 * Given a 5-value cron expression, compute the next time the cron job will run.
 * @param cronExp
 * @param startTime
 * @returns {Date} The datetime of the next cron run
 */
export function getNextRunFromCron(cronExp: string, startTime?: number): Date {
  // AWS EventBridge crons don't follow the standard 6-position expression that cron-parser supports
  // but they're similar enough to the 5-position one that we can just elide the final element.
  const options = {
    tz: "UTC",
    /**
     * In cron-parser, currentDate preserves the original cron expression's schedule;
     * whereas startDate will apply the schedule to the given startTime and shift
     * the hours to match the frequency.
     * The `startTime` check handles older format start_times that were hour values from 0-23.
     * Also, to get the correct nextRun, subtract 1 millisecond from the startTime
     * to ensure that cron-parser doesn't skip that block via its inclusive math
     */
    currentDate: startTime && startTime > toMilliseconds(23) ? new Date(startTime - 1) : undefined,
  }
  const parsedCronExp = cronExp.split(" ").slice(0, -1).join(" ")
  const interval = CronExpressionParser.parse(parsedCronExp, options)
  return interval.next().toDate()
}
/**
 * Returns the next hour, and 0 if the current hour is 23
 */
export function getNextHour(hour: number): number {
  return (hour + 1) % 24
}

/**
 * Returns the scheduled hour in UTC timestamp; if the scheduled hour is earlier than the current hour,
 * it will be scheduled for the next day. Properly handles daylight saving time transitions.
 */
export function getScheduledHourUTC(selectedHour?: number): number {
  const now = new Date()
  const currentHour = now.getHours()
  const operativeHour = selectedHour ?? getNextHour(currentHour)

  // Create target date starting with today
  const targetDate = new Date(now)

  // Clear minutes, seconds, and milliseconds to work with clean hours
  targetDate.setMinutes(0, 0, 0)

  // If the target hour is less than or equal to current hour, move to next day
  if (operativeHour <= currentHour) {
    targetDate.setDate(targetDate.getDate() + 1)
  }

  // Set the target hour - this will handle DST correctly since we're working in local time
  targetDate.setHours(operativeHour)

  // Convert to UTC timestamp
  return targetDate.getTime()
}

/**
 * Converts a Date or number to a number in seconds
 * @param date
 * @returns {number} The number in seconds
 */
export function toSeconds(date: Date | number): number {
  if (typeof date === "number") {
    return date / 1000
  }

  return date.getTime() / 1000
}

/**
 * Converts a Date or number to a number in milliseconds
 * @param date
 * @returns {number} The number in milliseconds
 */
export function toMilliseconds(date: Date | number): number {
  if (typeof date === "number") {
    return date * 1000
  }

  return date.getTime()
}
